import React, { useMemo, useRef } from 'react';
import { BackgroundThemeProps } from '../backgroundThemes';
import { useCanvasLoop } from '../useCanvasLoop';

interface CanvasSize {
  width: number;
  height: number;
}

interface Snowflake {
  x: number;
  y: number;
  depth: number;
  size: number;
  fallSpeed: number;
  drift: number;
  swayOffset: number;
  swaySpeed: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  spriteIndex: number;
  kind: 'round' | 'crystal';
}

const DESKTOP_PARTICLE_COUNT = 90;
const MOBILE_PARTICLE_COUNT = 45;
const STATIC_PARTICLE_COUNT = 40;
const SPRITE_SIZE = 64;

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);

const createRoundSprite = (centerColor: string, edgeColor: string) => {
  const sprite = document.createElement('canvas');
  sprite.width = sprite.height = SPRITE_SIZE;
  const ctx = sprite.getContext('2d');
  if (!ctx) return sprite;
  const center = SPRITE_SIZE / 2;
  const gradient = ctx.createRadialGradient(center, center, 0, center, center, center);
  gradient.addColorStop(0, centerColor);
  gradient.addColorStop(0.38, centerColor);
  gradient.addColorStop(1, edgeColor);
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(center, center, center, 0, Math.PI * 2);
  ctx.fill();
  return sprite;
};

const createCrystalSprite = (strokeColor: string, outlineColor: string) => {
  const sprite = document.createElement('canvas');
  sprite.width = sprite.height = SPRITE_SIZE;
  const ctx = sprite.getContext('2d');
  if (!ctx) return sprite;
  const center = SPRITE_SIZE / 2;
  const drawArms = (color: string, lineWidth: number) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    for (let index = 0; index < 6; index += 1) {
      const angle = (Math.PI / 3) * index;
      ctx.beginPath();
      ctx.moveTo(center, center);
      ctx.lineTo(center + Math.cos(angle) * 24, center + Math.sin(angle) * 24);
      ctx.stroke();
      [0.48, 0.72].forEach((branchStart) => {
        const baseX = center + Math.cos(angle) * 24 * branchStart;
        const baseY = center + Math.sin(angle) * 24 * branchStart;
        [-1, 1].forEach((direction) => {
          const branchAngle = angle + direction * (Math.PI / 4);
          ctx.beginPath();
          ctx.moveTo(baseX, baseY);
          ctx.lineTo(baseX + Math.cos(branchAngle) * 8, baseY + Math.sin(branchAngle) * 8);
          ctx.stroke();
        });
      });
    }
  };
  drawArms(outlineColor, 4.4);
  drawArms(strokeColor, 2.2);
  return sprite;
};

export const SnowfallBackground: React.FC<BackgroundThemeProps> = ({ tone, activity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null!);

  const renderer = useMemo(() => {
    let flakes: Snowflake[] = [];
    let roundSprites: HTMLCanvasElement[] = [];
    let crystalSprites: HTMLCanvasElement[] = [];
    const palette =
      tone === 'dark'
        ? {
            round: [
              ['rgba(255, 255, 255, 0.96)', 'rgba(255, 255, 255, 0)'],
              ['rgba(224, 242, 254, 0.9)', 'rgba(224, 242, 254, 0)'],
            ],
            crystal: [
              ['rgba(255, 255, 255, 0.94)', 'rgba(224, 242, 254, 0.2)'],
              ['rgba(224, 242, 254, 0.92)', 'rgba(255, 255, 255, 0.18)'],
            ],
          }
        : {
            round: [
              ['rgba(148, 163, 184, 0.78)', 'rgba(148, 163, 184, 0)'],
              ['rgba(125, 211, 252, 0.74)', 'rgba(125, 211, 252, 0)'],
            ],
            crystal: [
              ['rgba(148, 163, 184, 0.86)', 'rgba(71, 85, 105, 0.22)'],
              ['rgba(125, 211, 252, 0.82)', 'rgba(51, 65, 85, 0.2)'],
            ],
          };

    const createFlake = (size: CanvasSize, aboveTop: boolean): Snowflake => {
      const depth = Math.random();
      const depthScale = 0.45 + depth * 0.55;
      const kind = Math.random() < 0.75 ? 'round' : 'crystal';

      return {
        x: randomBetween(0, size.width),
        y: aboveTop ? randomBetween(-size.height * 0.4, -24) : randomBetween(0, size.height),
        depth,
        size:
          kind === 'round' ? randomBetween(3, 5) + depth * 4 : randomBetween(10, 14) + depth * 8,
        fallSpeed: randomBetween(15, 55) * depthScale,
        drift: randomBetween(8, 28) * (0.6 + depth * 0.6),
        swayOffset: randomBetween(0, Math.PI * 2),
        swaySpeed: randomBetween(0.45, 0.95),
        rotation: randomBetween(0, Math.PI * 2),
        rotationSpeed: kind === 'crystal' ? randomBetween(-0.4, 0.4) : 0,
        opacity: randomBetween(0.36, 0.86) * depthScale,
        spriteIndex: Math.floor(Math.random() * palette.round.length),
        kind,
      };
    };
    const drawFlake = (ctx: CanvasRenderingContext2D, flake: Snowflake) => {
      const sprites = flake.kind === 'round' ? roundSprites : crystalSprites;
      const sprite = sprites[flake.spriteIndex];
      if (!sprite) return;

      const sway = Math.sin(flake.swayOffset + flake.y * 0.01) * flake.drift;
      const x = flake.x + sway;
      if (flake.y < -flake.size) return;
      ctx.save();
      ctx.globalAlpha = flake.opacity;
      ctx.translate(x, flake.y);
      if (flake.kind === 'crystal') {
        ctx.rotate(flake.rotation);
      }
      ctx.drawImage(sprite, -flake.size / 2, -flake.size / 2, flake.size, flake.size);
      ctx.restore();
    };
    return {
      setup(_ctx: CanvasRenderingContext2D, size: CanvasSize) {
        roundSprites = palette.round.map(([center, edge]) => createRoundSprite(center, edge));
        crystalSprites = palette.crystal.map(([stroke, outline]) =>
          createCrystalSprite(stroke, outline)
        );
        const count = size.width >= 768 ? DESKTOP_PARTICLE_COUNT : MOBILE_PARTICLE_COUNT;
        flakes = Array.from({ length: count }, () => createFlake(size, false));
      },
      draw(ctx: CanvasRenderingContext2D, size: CanvasSize, deltaMs: number) {
        const elapsedSeconds = deltaMs / 1000;
        const wind = Math.sin(performance.now() * 0.0001) * 10;
        ctx.clearRect(0, 0, size.width, size.height);
        flakes.forEach((flake) => {
          flake.y += flake.fallSpeed * elapsedSeconds;
          flake.x += wind * elapsedSeconds;
          flake.swayOffset += flake.swaySpeed * elapsedSeconds;
          flake.rotation += flake.rotationSpeed * elapsedSeconds;

          const margin = flake.size + flake.drift;
          if (flake.x < -margin) flake.x = size.width + margin;
          if (flake.x > size.width + margin) flake.x = -margin;
          if (flake.y > size.height + flake.size) Object.assign(flake, createFlake(size, true));
          drawFlake(ctx, flake);
        });
      },
      drawStatic(ctx: CanvasRenderingContext2D, size: CanvasSize) {
        ctx.clearRect(0, 0, size.width, size.height);
        Array.from({ length: STATIC_PARTICLE_COUNT }, () => createFlake(size, false)).forEach(
          (flake) => drawFlake(ctx, flake)
        );
      },
    };
  }, [tone]);

  useCanvasLoop(canvasRef, renderer, activity);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
};
